"use client";

import * as React from "react";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  FileUp,
  Trash2,
  FileText,
  Loader2,
  AlertCircle,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import {
  PdfHubConnection,
  PdfChunkProgress,
  PdfProcessingStarted,
} from "@/lib/pdf-signalr";
import JSZip from "jszip";
import {
  ApiError,
  getPdfPageCount,
  calculateExpectedChunkCount,
  calculateExpectedChunks,
  requestUploadUrl,
  uploadToMinIO,
  confirmUpload,
  splitPdf,
  getChunkDownloadUrl,
  getStorageQuota,
  formatFileSize,
  MAX_FILE_SIZE_BYTES,
  type DocumentInfo,
  type ChunkInfo,
} from "@/lib/pdf-splitter-api";
import { StorageGauge } from "./storage-gauge";
import { SplitOptionsForm } from "./split-options-form";
import { ChunkList } from "./chunk-list";
import type { SplitOptions, StorageQuotaData, ValidationErrors } from "./types";

export function PdfSplitter() {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [clientPageCount, setClientPageCount] = useState<number | null>(null);
  const [isCountingPages, setIsCountingPages] = useState(false);
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  const [splitOptions, setSplitOptions] = useState<SplitOptions>({
    mode: "by-page-count",
    pagesPerChunk: 50,
    overlapPages: 2,
    maxFileSizeMB: 10,
    customRanges: "",
  });
  const [chunks, setChunks] = useState<ChunkInfo[]>([]);
  const [plannedChunks, setPlannedChunks] = useState<ChunkInfo[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<string | null>(null);
  const [storageQuota, setStorageQuota] = useState<StorageQuotaData | null>(null);
  const [quotaLoadFailed, setQuotaLoadFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validationErrors = useMemo<ValidationErrors>(() => {
    const errors: ValidationErrors = {};

    if (splitOptions.mode === "by-page-count") {
      const pages = splitOptions.pagesPerChunk;
      if (pages === undefined || pages === null || isNaN(pages)) {
        errors.pagesPerChunk = "Please enter a number";
      } else if (pages < 5) {
        errors.pagesPerChunk = "Minimum is 5 pages";
      } else if (pages > 200) {
        errors.pagesPerChunk = "Maximum is 200 pages";
      }

      const overlap = splitOptions.overlapPages;
      if (overlap === undefined || overlap === null || isNaN(overlap)) {
        errors.overlapPages = "Please enter a number";
      } else if (overlap < 0) {
        errors.overlapPages = "Cannot be negative";
      } else if (overlap > 10) {
        errors.overlapPages = "Maximum is 10 pages";
      } else if (pages && overlap >= pages) {
        errors.overlapPages = "Must be less than pages per chunk";
      }
    }

    if (splitOptions.mode === "by-file-size") {
      const size = splitOptions.maxFileSizeMB;
      if (size === undefined || size === null || isNaN(size)) {
        errors.maxFileSizeMB = "Please enter a number";
      } else if (size < 1) {
        errors.maxFileSizeMB = "Minimum is 1 MB";
      } else if (size > 100) {
        errors.maxFileSizeMB = "Maximum is 100 MB";
      }
    }

    return errors;
  }, [splitOptions]);

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const fetchQuota = useCallback(async () => {
    try {
      const quota = await getStorageQuota();
      setStorageQuota(quota.storage);
      setQuotaLoadFailed(false);
    } catch (err) {
      console.warn("Failed to fetch quota (CORS?):", err);
      setStorageQuota(null);
      setQuotaLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    let active = true;

    getStorageQuota()
      .then((quota) => {
        if (!active) return;
        setStorageQuota(quota.storage);
        setQuotaLoadFailed(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        console.warn("Failed to fetch quota (CORS?):", err);
        setStorageQuota(null);
        setQuotaLoadFailed(true);
      });

    return () => {
      active = false;
    };
  }, []);

  // SignalR event handlers
  const handleProcessingStarted = useCallback((data: PdfProcessingStarted) => {
    const placeholders: ChunkInfo[] = Array.from(
      { length: data.estimatedChunks },
      (_, i) => ({
        chunkId: `pending-${i + 1}`,
        chunkNumber: i + 1,
        startPage: 0,
        endPage: 0,
        fileSizeBytes: 0,
        status: "pending" as const,
      }),
    );
    setPlannedChunks(placeholders);
    setProcessingProgress(0);
  }, []);

  const handleChunkCreated = useCallback((data: PdfChunkProgress) => {
    setPlannedChunks((prev) => {
      const updated = [...prev];
      const index = updated.findIndex(
        (c) => c.chunkNumber === data.chunkNumber,
      );
      if (index !== -1) {
        updated[index] = {
          ...updated[index],
          chunkId: `chunk-${data.chunkNumber}`,
          startPage: data.startPage,
          endPage: data.endPage,
          fileSizeBytes: data.fileSizeBytes,
          fileName: data.fileName,
          status: "ready",
        };
      }
      return updated;
    });

    setProcessingProgress(data.progressPercent);
  }, []);

  const handleProcessingCompleted = useCallback(
    (_data: { documentId: string; totalChunks: number; durationMs: number }) => {
      setProcessingProgress(100);
    },
    [],
  );

  const handleProcessingFailed = useCallback(
    (data: { documentId: string; errorMessage: string }) => {
      setError(data.errorMessage);
      setPlannedChunks([]);
    },
    [],
  );

  useEffect(() => {
    if (!isProcessing || !documentInfo?.documentId) return;

    let unsubProcessingStarted: (() => void) | undefined;
    let unsubChunkCreated: (() => void) | undefined;
    let unsubProcessingCompleted: (() => void) | undefined;
    let unsubProcessingFailed: (() => void) | undefined;

    const setupSignalR = async () => {
      try {
        await PdfHubConnection.initialize();
        await PdfHubConnection.subscribeToDocument(documentInfo.documentId);

        unsubProcessingStarted = PdfHubConnection.on(
          "ProcessingStarted",
          handleProcessingStarted,
        );
        unsubChunkCreated = PdfHubConnection.on(
          "ChunkCreated",
          handleChunkCreated,
        );
        unsubProcessingCompleted = PdfHubConnection.on(
          "ProcessingCompleted",
          handleProcessingCompleted,
        );
        unsubProcessingFailed = PdfHubConnection.on(
          "ProcessingFailed",
          handleProcessingFailed,
        );
      } catch (err) {
        console.error("[SignalR] Failed to connect:", err);
      }
    };

    setupSignalR();

    return () => {
      unsubProcessingStarted?.();
      unsubChunkCreated?.();
      unsubProcessingCompleted?.();
      unsubProcessingFailed?.();
      if (documentInfo?.documentId) {
        PdfHubConnection.unsubscribeFromDocument(documentInfo.documentId);
      }
    };
  }, [
    isProcessing,
    documentInfo?.documentId,
    handleProcessingStarted,
    handleChunkCreated,
    handleProcessingCompleted,
    handleProcessingFailed,
  ]);

  useEffect(() => {
    if (retryAfter && retryAfter > 0) {
      const timer = setInterval(() => {
        setRetryAfter((prev) => (prev && prev > 0 ? prev - 1 : null));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [retryAfter]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Invalid file type: Please upload a PDF file");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File too large: Maximum size is ${formatFileSize(MAX_FILE_SIZE_BYTES)}`);
      return;
    }

    setUploadedFile(file);
    setError(null);
    setChunks([]);
    setPlannedChunks([]);
    setClientPageCount(null);

    setIsCountingPages(true);
    try {
      const pageCount = await getPdfPageCount(file);
      setClientPageCount(pageCount);
      toast.success(`PDF loaded: ${file.name} has ${pageCount} pages`);
    } catch {
      toast.info("Could not read page count - will be determined after upload");
    } finally {
      setIsCountingPages(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadedFile) return;

    setIsUploading(true);
    setError(null);

    try {
      const { documentId, uploadUrl } = await requestUploadUrl(
        uploadedFile.name,
        uploadedFile.size,
        clientPageCount ? { totalPages: clientPageCount } : undefined,
      );

      setDocumentInfo({
        documentId,
        fileName: uploadedFile.name,
        totalPages: clientPageCount || 0,
        fileSizeBytes: uploadedFile.size,
        status: "uploading",
      });

      await uploadToMinIO(uploadUrl, uploadedFile);

      const { totalPages } = await confirmUpload(documentId);

      setDocumentInfo((prev) =>
        prev ? { ...prev, totalPages, status: "uploaded" } : null,
      );

      toast.success(`PDF uploaded with ${totalPages} pages. Ready to split!`);
      fetchQuota();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to upload PDF";
      const retryTime = err instanceof ApiError ? err.retryAfter : null;
      setError(errorMessage);
      setRetryAfter(retryTime || null);
      setDocumentInfo(null);
      toast.error(retryTime ? `Upload failed: ${errorMessage}. Retry in ${retryTime}s` : `Upload failed: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSplit = async () => {
    if (!documentInfo) return;

    setIsProcessing(true);
    setError(null);
    setPlannedChunks([]);
    setProcessingProgress(0);

    try {
      setDocumentInfo((prev) =>
        prev ? { ...prev, status: "splitting" } : null,
      );

      const expectedChunks = calculateExpectedChunks(
        documentInfo.totalPages,
        splitOptions.pagesPerChunk || 50,
        splitOptions.overlapPages || 0,
      );

      setPlannedChunks(
        expectedChunks.map((c) => ({
          chunkId: `pending-${c.chunkNumber}`,
          chunkNumber: c.chunkNumber,
          startPage: c.startPage,
          endPage: c.endPage,
          fileSizeBytes: 0,
          status: "pending" as const,
        })),
      );

      const result = await splitPdf(documentInfo.documentId, splitOptions);

      setChunks(result.chunks.map((c) => ({ ...c, status: "ready" as const })));
      setPlannedChunks([]);
      setDocumentInfo((prev) => (prev ? { ...prev, status: "completed" } : null));

      toast.success(`PDF split into ${result.chunks.length} chunks. Ready for download!`);
      fetchQuota();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to split PDF";
      const retryTime = err instanceof ApiError ? err.retryAfter : null;
      setError(errorMessage);
      setRetryAfter(retryTime || null);
      setDocumentInfo((prev) => (prev ? { ...prev, status: "failed" } : null));
      setPlannedChunks([]);
      toast.error(retryTime ? `Split failed: ${errorMessage}. Retry in ${retryTime}s` : `Split failed: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadChunk = async (chunk: ChunkInfo) => {
    if (!documentInfo || chunk.status !== "ready") return;

    try {
      const { downloadUrl } = await getChunkDownloadUrl(
        documentInfo.documentId,
        chunk.chunkId,
      );
      const fileName =
        chunk.fileName ||
        `${documentInfo.fileName.replace(".pdf", "")}_part${chunk.chunkNumber}.pdf`;

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${fileName}`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to download chunk";
      toast.error(`Download failed: ${errorMessage}`);
    }
  };

  const handleDownloadAll = async () => {
    if (!documentInfo) return;

    const readyChunks = chunks.filter((c) => c.status === "ready");
    if (readyChunks.length === 0) return;

    setIsDownloading(true);
    setDownloadProgress(`Preparing download (0/${readyChunks.length})...`);

    try {
      const zip = new JSZip();
      const baseFileName = documentInfo.fileName.replace(".pdf", "");

      for (let i = 0; i < readyChunks.length; i++) {
        const chunk = readyChunks[i];
        setDownloadProgress(
          `Downloading chunk ${i + 1}/${readyChunks.length}...`,
        );

        const { downloadUrl } = await getChunkDownloadUrl(
          documentInfo.documentId,
          chunk.chunkId,
        );
        const response = await fetch(downloadUrl);

        if (!response.ok) {
          throw new Error(`Failed to download chunk ${chunk.chunkNumber}`);
        }

        const blob = await response.blob();
        const fileName =
          chunk.fileName || `${baseFileName}_part${chunk.chunkNumber}.pdf`;

        zip.file(fileName, blob);
      }

      setDownloadProgress("Creating ZIP archive...");

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${baseFileName}_chunks.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${readyChunks.length} chunks as ZIP archive`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create ZIP archive";
      toast.error(`Download failed: ${errorMessage}`);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setClientPageCount(null);
    setDocumentInfo(null);
    setChunks([]);
    setPlannedChunks([]);
    setError(null);
    setProcessingProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const displayChunks = chunks.length > 0 ? chunks : plannedChunks;

  const estimatedChunks = useMemo(() => {
    const totalPages = clientPageCount || documentInfo?.totalPages;
    if (!totalPages) return 0;

    if (splitOptions.mode === "by-page-count" && splitOptions.pagesPerChunk) {
      return calculateExpectedChunkCount(
        totalPages,
        splitOptions.pagesPerChunk,
        splitOptions.overlapPages || 0,
      );
    }

    if (splitOptions.mode === "by-file-size" && splitOptions.maxFileSizeMB) {
      return Math.ceil(
        (documentInfo?.fileSizeBytes || uploadedFile?.size || 0) /
          (splitOptions.maxFileSizeMB * 1024 * 1024),
      );
    }

    return 0;
  }, [
    clientPageCount,
    documentInfo?.totalPages,
    documentInfo?.fileSizeBytes,
    uploadedFile?.size,
    splitOptions.mode,
    splitOptions.pagesPerChunk,
    splitOptions.overlapPages,
    splitOptions.maxFileSizeMB,
  ]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>PDF Splitter</CardTitle>
        <CardDescription>
          Split large PDF documents into smaller chunks.
        </CardDescription>
        <div className="mt-3 p-3 bg-muted/50 rounded-md text-xs text-muted-foreground space-y-3">
          <div className="space-y-1">
            <p>
              <strong>Usage limits:</strong>
            </p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>Max file size: 300 MB</li>
              <li>3 uploads per minute, 20 per hour</li>
            </ul>
          </div>
          <StorageGauge storageQuota={storageQuota} quotaLoadFailed={quotaLoadFailed} />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* File Upload Section */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Upload PDF File</Label>
            <div className="flex items-center gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="pdf-upload"
              />
              <div className="flex-1">
                {uploadedFile ? (
                  <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">
                          {uploadedFile.name}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{formatFileSize(uploadedFile.size)}</span>
                          {isCountingPages ? (
                            <span className="flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Reading pages...
                            </span>
                          ) : clientPageCount ? (
                            <span className="text-primary font-medium">
                              {clientPageCount} pages
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setUploadedFile(null);
                        setClientPageCount(null);
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full p-8 border-2 border-dashed rounded-lg hover:bg-muted/50 transition-colors flex flex-col items-center gap-2 text-muted-foreground"
                  >
                    <FileUp className="h-8 w-8" />
                    <div className="text-center">
                      <p className="text-sm font-medium">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs">
                        PDF files only (max{" "}
                        {formatFileSize(MAX_FILE_SIZE_BYTES)})
                      </p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>

          {uploadedFile && !documentInfo && (
            <Button
              onClick={handleUpload}
              disabled={isUploading || isCountingPages}
              className="w-full"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <FileUp className="h-4 w-4 mr-2" />
                  Upload PDF
                </>
              )}
            </Button>
          )}
        </div>

        {/* Document Info */}
        {documentInfo && (
          <div className="p-4 border rounded-lg bg-muted/50 space-y-2">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h3 className="font-medium">Document Information</h3>
                <div className="text-sm text-muted-foreground space-y-0.5">
                  <p>File: {documentInfo.fileName}</p>
                  <p>Total Pages: {documentInfo.totalPages}</p>
                  <p>File Size: {formatFileSize(documentInfo.fileSizeBytes)}</p>
                  {estimatedChunks > 0 && chunks.length === 0 && (
                    <p>Estimated Chunks: {estimatedChunks}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {documentInfo.status === "uploading" && (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-xs">Uploading...</span>
                  </div>
                )}
                {documentInfo.status === "uploaded" && (
                  <div className="flex items-center gap-1.5 text-green-600">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-xs">Ready to split</span>
                  </div>
                )}
                {documentInfo.status === "splitting" && (
                  <div className="flex items-center gap-1.5 text-blue-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-xs">Splitting...</span>
                  </div>
                )}
                {documentInfo.status === "completed" && (
                  <div className="flex items-center gap-1.5 text-green-600">
                    <Check className="h-4 w-4" />
                    <span className="text-xs">Completed</span>
                  </div>
                )}
                {documentInfo.status === "failed" && (
                  <div className="flex items-center gap-1.5 text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-xs">Failed</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Split Options */}
        {documentInfo && chunks.length === 0 && !isProcessing && (
          <SplitOptionsForm
            splitOptions={splitOptions}
            onSplitOptionsChange={setSplitOptions}
            validationErrors={validationErrors}
            hasValidationErrors={hasValidationErrors}
            isProcessing={isProcessing}
            isUploadReady={documentInfo.status === "uploaded"}
            onSplit={handleSplit}
          />
        )}

        {/* Chunks List with Progress */}
        <ChunkList
          displayChunks={displayChunks}
          isProcessing={isProcessing}
          hasRealChunks={chunks.length > 0}
          isDownloading={isDownloading}
          downloadProgress={downloadProgress}
          processingProgress={processingProgress}
          onDownloadChunk={handleDownloadChunk}
          onDownloadAll={handleDownloadAll}
        />

        {/* Error Display */}
        {error && (
          <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">Error</p>
              <p className="text-sm text-destructive/90">{error}</p>
              {retryAfter && retryAfter > 0 && (
                <p className="text-sm text-destructive/70 mt-1">
                  You can retry in {retryAfter} seconds...
                </p>
              )}
              {retryAfter === 0 && (
                <p className="text-sm text-green-600 mt-1">
                  You can retry now!
                </p>
              )}
            </div>
          </div>
        )}

        {/* Reset Button */}
        {(documentInfo || uploadedFile) && (
          <Button onClick={handleReset} variant="outline" className="w-full">
            <Trash2 className="h-4 w-4 mr-2" />
            Reset
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
